/**
 * 지출결의등록(type1) — 사업(부서+사업) 집계 · 1사업·다과목 = 1결의 조립
 */

import { loadExpenseBalanceRows } from 'expense/expenseLoadUtils';
import { listSubjectsForProject } from 'expense/resolutionByProjectUtils';
import {
  createResolutionUid,
  createResolutionDetailUid,
  emptyResolutionMaster,
  emptyResolutionDetailRow,
  EXPENSE_STATUS,
  todayYmd,
} from 'expense/expenseStore';

/**
 * 예산확정(현액)을 부서+사업(+옵션 재원)으로 집계
 * @param {{ includeFund?: boolean, deptCode?: string }} [opts]
 */
export function listProjectsAggregated(tenantCode, fiscalYear, opts = {}) {
  const includeFund = !!opts.includeFund;
  const deptFilter = String(opts.deptCode || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const balances = loadExpenseBalanceRows(tenantCode, fiscalYear) || [];
  const byKey = new Map();

  balances.forEach((r) => {
    const deptCode = String(r.deptCode || '').trim();
    const projectCode = String(r.projectCode || '').trim();
    if (!projectCode) return;
    if (deptFilter.length && !deptFilter.includes(deptCode)) return;
    const fundCode = includeFund ? String(r.fundCode || '').trim() : '';
    const key = `${deptCode}|${projectCode}|${fundCode}`;
    const prev = byKey.get(key);
    const balanceAmt = Number(r.balanceAmt) || 0;
    const availableAmt = Number(r.availableAmt ?? r.balanceAmt) || 0;
    if (!prev) {
      byKey.set(key, {
        id: key,
        deptCode,
        deptName: String(r.deptName || '').trim(),
        projectCode,
        projectName: String(r.projectName || '').trim(),
        fundCode: includeFund ? fundCode : '',
        fundName: includeFund ? String(r.fundName || '').trim() : '',
        balanceAmt,
        availableAmt,
        subjectCount: 1,
      });
      return;
    }
    prev.balanceAmt += balanceAmt;
    prev.availableAmt += availableAmt;
    prev.subjectCount += 1;
    if (!prev.deptName && r.deptName) prev.deptName = String(r.deptName).trim();
    if (!prev.projectName && r.projectName) prev.projectName = String(r.projectName).trim();
  });

  return [...byKey.values()].sort((a, b) => {
    const d = String(a.deptCode).localeCompare(String(b.deptCode), 'ko');
    if (d) return d;
    return String(a.projectCode).localeCompare(String(b.projectCode), 'ko');
  });
}

/**
 * 선택 사업(부서+사업[+재원])에 편성된 과목 목록
 * — 사업집계 행의 축과 동일한 현액만 반환
 */
export function listSubjectsForSelectedProject(tenantCode, fiscalYear, projectRow = {}) {
  const projectCode = String(projectRow?.projectCode || '').trim();
  if (!projectCode) return [];

  const deptCode = String(projectRow?.deptCode || '').trim();
  const fundCode = String(projectRow?.fundCode || '').trim();

  let rows = listSubjectsForProject(tenantCode, fiscalYear, projectCode, projectRow || {});
  if (deptCode) {
    rows = rows.filter((r) => String(r.deptCode || '').trim() === deptCode);
  }
  if (fundCode) {
    rows = rows.filter((r) => String(r.fundCode || '').trim() === fundCode);
  }
  return rows;
}

export { listSubjectsForProject };

/**
 * 채주 합계를 선택 과목에 배분 (applyAmt 있으면 우선, 없으면 균등)
 */
export function allocateSubjectAmounts(subjectRows = [], payeeTotal = 0) {
  const list = (subjectRows || []).filter(Boolean);
  if (!list.length) return [];
  const withApply = list.filter((s) => Number(s.applyAmt) > 0);
  if (withApply.length) {
    return list.map((s) => ({
      ...s,
      applyAmt: Number(s.applyAmt) > 0 ? Number(s.applyAmt) : 0,
    }));
  }
  const n = list.length;
  const base = Math.floor(Number(payeeTotal || 0) / n);
  let remain = Number(payeeTotal || 0) - base * n;
  return list.map((s, i) => {
    const extra = i < remain ? 1 : 0;
    return { ...s, applyAmt: base + extra };
  });
}

/**
 * 1사업 · N과목 · 채주(디테일) → 단일 결의 초안
 */
export function buildType1Resolution({
  fiscalYear,
  projectRow,
  subjectRows = [],
  payeeRows = [],
  resolutionUid = '',
  remark = '',
}) {
  const uid = String(resolutionUid || '').trim() || createResolutionUid();
  const payees = (payeeRows || []).filter((r) => r && r.mod !== 'D');
  const payeeTotal = payees.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const allocated = allocateSubjectAmounts(subjectRows, payeeTotal);
  const primary = allocated[0] || {};
  const subjectLabel =
    allocated.length > 1
      ? `${primary.subjectName || primary.subjectCode || ''} 외 ${allocated.length - 1}`
      : primary.subjectName || '';

  const master = emptyResolutionMaster({
    resolutionUid: uid,
    fiscalYear,
    deptCode: projectRow?.deptCode || primary.deptCode || '',
    deptName: projectRow?.deptName || primary.deptName || '',
    projectCode: projectRow?.projectCode || '',
    projectName: projectRow?.projectName || '',
    subjectCode: primary.subjectCode || '',
    subjectName: subjectLabel,
    fundCode: projectRow?.fundCode || primary.fundCode || '',
    fundName: projectRow?.fundName || primary.fundName || '',
    balanceUid: primary.balanceUid || '',
    resolutionAmt: payeeTotal,
    resolutionDate: todayYmd(),
    registerDate: todayYmd(),
    remark:
      remark ||
      (allocated.length > 1
        ? `type1 다과목(${allocated.map((s) => s.subjectCode).join(',')})`
        : ''),
    status: EXPENSE_STATUS.DRAFT,
    /** type1 전용 — 연결된 과목 축 */
    registerLayout: 'TYPE1',
    subjectLines: allocated.map((s) => ({
      subjectCode: s.subjectCode || '',
      subjectName: s.subjectName || '',
      balanceUid: s.balanceUid || '',
      fundCode: s.fundCode || '',
      fundName: s.fundName || '',
      balanceAmt: Number(s.balanceAmt) || 0,
      availableAmt: Number(s.availableAmt) || 0,
      applyAmt: Number(s.applyAmt) || 0,
    })),
    mod: 'A',
  });

  const details = payees.map((p) =>
    emptyResolutionDetailRow({
      ...p,
      detailUid: p.detailUid || createResolutionDetailUid(),
      resolutionUid: uid,
      amount: Number(p.amount) || 0,
      mod: p.mod === 'U' ? 'U' : 'A',
    })
  );

  return { master, details, payeeTotal, allocated };
}
